/**
 * The countdown (D153) — the number under the clock, animated as it hits.
 *
 * @remarks
 * Mike's ask, over the arc doc's own "readings are events, never a gauge":
 * the readings are dread, a number is urgency, and he asked for urgency by
 * name. So: one number, in the system palette, under the instrument's own
 * label — `ATMOSPHERE`, the same machine that speaks the readings, never a
 * game HUD — and the accent green stays spent on the one thing it is for.
 *
 * The engine never computes seconds. It answers TURNS remaining to the
 * deadline; the shell knows the rate and when the current silence began, and
 * this component does the one multiplication, ticking once a second between
 * turns. With the clock off there are no seconds to show, so the number is
 * honest and says turns.
 *
 * Calder's three rules, in order: the pulse is motion and reduced motion
 * REMOVES it (the number still changes; it stops jumping — see the shell's
 * stylesheet); it is NOT a live region (`role="timer"`, `aria-live="off"` —
 * a number changing every second in a live region is a screen reader reading
 * nothing else, and the transcript's readings already carry the information
 * where that reader is listening); and it does not exist before the clock
 * starts — the shell renders it only while `resolvePacing` returns a
 * countdown, and there is no second flag.
 *
 * @packageDocumentation
 */

import { useEffect, useState } from 'react';

/** Props for {@link CountdownComponent}. */
export interface CountdownComponentProps {
	/** Milliseconds of silence that cost one turn (the pack's rate). */
	readonly autoWaitMs: number;
	/** The instrument's name — `ATMOSPHERE`. */
	readonly label: string;
	/**
	 * The shell's clock (`performance.now` by default) — injectable so the
	 * seconds can be pinned in a test without faking the world's timers.
	 */
	readonly now?: () => number;
	/**
	 * When the CURRENT silence began, on the same clock as `now` — present
	 * while the metronome runs. Absent, the clock is off and the number is
	 * turns rather than seconds.
	 */
	readonly turnStartedAt?: number;
	/** Turns until the deadline fires, the firing turn included. */
	readonly turnsRemaining: number;
}

/** How often the seconds re-read the clock. */
const TICK_MS = 1000;

/** The shell's clock — one stable reference, so the effect keyed on it never re-arms for nothing. */
const readPerformanceClock = (): number => performance.now();

/** `M:SS` — a clock face, not a stopwatch. */
const formatSeconds = (totalSeconds: number): string => {
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return `${String(minutes)}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Render the countdown.
 *
 * @param props - {@link CountdownComponentProps}
 * @returns The timer
 */
export const CountdownComponent = ({
	autoWaitMs,
	label,
	now = readPerformanceClock,
	turnsRemaining,
	turnStartedAt,
}: CountdownComponentProps) => {
	const [readAt, setReadAt] = useState(now);

	// Tick only while the metronome runs; a turns-only display has nothing to
	// re-read. Re-reading on every turn start keeps the first second honest.
	useEffect(() => {
		if (turnStartedAt === undefined) {
			return undefined;
		}

		setReadAt(now());

		const interval = setInterval(() => {
			setReadAt(now());
		}, TICK_MS);

		return () => {
			clearInterval(interval);
		};
	}, [now, turnStartedAt]);

	const displayed =
		turnStartedAt === undefined
			? `${String(turnsRemaining)} ${turnsRemaining === 1 ? 'TURN' : 'TURNS'}`
			: formatSeconds(Math.max(0, Math.ceil((turnsRemaining * autoWaitMs - (readAt - turnStartedAt)) / 1000)));

	return (
		<div aria-label={`${label} countdown`} aria-live="off" className="countdown" role="timer">
			<span className="countdown__label">{label}</span>
			{/* One span per character, keyed on its position AND its value: a
			    character that changed remounts and hits; one that did not keeps
			    its element and holds still (Mike: only the digits that move should
			    move). The whole reading stays one text node for copy and for the
			    accessibility tree. */}
			<span className="countdown__value">
				{[...displayed].map((character, index) => (
					<span className="countdown__character" key={`${String(index)}:${character}`}>
						{character}
					</span>
				))}
			</span>
		</div>
	);
};
